/**
 * agentLetters.ts — Cover letter & application letter generation via ai-companion
 *
 * Uses JWT-authed ai-companion with a dedicated system_override so the chat
 * persona ("do not write the letter inline") cannot truncate the draft.
 *
 * IMPORTANT: We do NOT pass json_only:true because that forces
 * responseMimeType:"application/json" on the Gemini API, constraining the model
 * to output JSON and causing letter truncation. Instead we rely on system_override
 * to prevent action-tag generation; the edge function's action parser gracefully
 * falls through and returns { content } when no action is found.
 */

import { supabase } from '../../lib/supabase';
import { resolveResponseLanguage } from '../../services/contentTranslation';

export interface LetterRequest {
  mode: 'cover' | 'application';
  resume: string;
  jobTitle: string;
  company: string;
  tone?: string;
  referredBy?: string;
  locale?: string;
}

export interface LetterResult {
  letter: string;
  tokensUsed?: number;
}

const COVER_LETTER_SYSTEM = `You are a professional resume writer. Your ONLY job is to write a complete cover letter.

RULES:
- Output ONLY the letter body text.
- Do NOT include a date, salutation ("Dear..."), sign-off, or signature.
- Do NOT output JSON, action tags, markdown fences, or meta-commentary.
- Do NOT say "Here is your cover letter" or anything similar.

STRUCTURE (exactly 3 paragraphs, 250–400 words total):
Paragraph 1 — Opening hook: State who the candidate is, the specific role, and one compelling reason they are a great fit.
Paragraph 2 — Evidence: Cite 2–3 specific achievements (with numbers, tools, or project names) from the resume that directly match the job requirements.
Paragraph 3 — Closing: Express enthusiasm, restate fit, and request a conversation or next steps. End with a complete sentence.

CRITICAL: You MUST write all 3 paragraphs in full. Never stop mid-sentence. Never truncate. The last character of your output must be a period or exclamation mark.`;

const APPLICATION_LETTER_SYSTEM = `You are a professional resume writer. Your ONLY job is to write a complete application note.

RULES:
- Output ONLY the letter body text.
- Do NOT include a date, salutation, greeting, sign-off, or signature.
- Do NOT output JSON, action tags, markdown fences, or meta-commentary.

STRUCTURE (exactly 3 paragraphs, 200–350 words total):
Paragraph 1 — Introduction: State who you are and the role you are applying for (mention referral if provided).
Paragraph 2 — Evidence: Cite 2 concrete achievements from the resume that match the role requirements.
Paragraph 3 — Closing: Request next steps. End with a complete sentence.

CRITICAL: You MUST write all 3 paragraphs in full. Never stop mid-sentence. Never truncate. The last character of your output must be a period or exclamation mark.`;

export function letterLooksLikeSafetyRefusal(letter: string): boolean {
  const lower = letter.trim().toLowerCase();
  const refusalPrefixes = [
    "i'm sorry",
    'i am sorry',
    'i cannot',
    "i can't",
    "i'm unable",
    'i am unable',
    "i don't have",
    'i do not have',
    'unfortunately',
    'as an ai',
    'as a language model',
    'i apologize',
    'i need more information',
    'please provide',
  ];
  return refusalPrefixes.some((prefix) => lower.startsWith(prefix)) || lower.length < 80;
}

/** True when the model cut off mid-sentence or returned only a fragment. */
export function letterLooksIncomplete(letter: string): boolean {
  const t = letter.replace(/\s+/g, ' ').trim();
  if (t.length < 200) return true;
  const last = t.slice(-1);
  if (!/[.!?'"]/.test(last)) return true;
  const paragraphs = t.split(/\n\s*\n/).filter(p => p.trim().length > 30);
  if (paragraphs.length < 2) return true;
  return false;
}

function cleanLetter(raw: string): string {
  return raw
    .replace(/\[STATUS:[^\]]*\]/g, '')
    .replace(/\{\s*"action"\s*:\s*"[^"]+"[\s\S]*?\}/g, '')
    .replace(/^```(?:\w+)?\s*|\s*```$/g, '')
    .replace(/^(Here is|Here's|Below is)[^:\n]*:\s*/i, '')
    .replace(/^(Dear\s+[^,\n]+,?\s*\n?)/i, '')
    .trim();
}

/** Build a quality fallback letter when the LLM fails or truncates. */
export function buildFallbackLetter(
  mode: 'cover' | 'application',
  jobTitle: string,
  company: string,
  resumeSnippet: string,
  opts?: { referredBy?: string; candidateName?: string },
): string {
  const skills = extractTopSkillsFromResume(resumeSnippet, 3);
  const years = extractYearsOfExperience(resumeSnippet);

  if (mode === 'cover') {
    return [
      `I am excited to apply for the ${jobTitle} position at ${company}. With ${years} years of professional experience and expertise in ${skills}, I am confident in my ability to make an immediate and meaningful contribution to your team.`,
      '',
      `Throughout my career, I have consistently delivered results by driving projects from concept to completion, collaborating with cross-functional teams, and implementing solutions that improve efficiency and outcomes. My expertise in ${skills} has prepared me well to tackle the challenges of this role and exceed expectations.`,
      '',
      `I would welcome the opportunity to discuss how my experience and skills align with your team's goals. I am eager to bring my passion and expertise to ${company} and contribute to your continued success. I look forward to hearing from you.`,
    ].join('\n');
  }

  const referral = opts?.referredBy ? `, referred by ${opts.referredBy}` : '';
  return [
    `I am writing to express my strong interest in the ${jobTitle} position at ${company}${referral}. With ${years} years of relevant experience and expertise in ${skills}, I believe I am well-suited for this opportunity.`,
    '',
    `My professional background includes delivering measurable results through technical expertise and cross-functional collaboration. I have consistently applied my skills to drive successful outcomes in complex, fast-paced environments.`,
    '',
    `I would appreciate the opportunity to discuss how my qualifications align with this role. I look forward to the next steps in the process.`,
  ].join('\n');
}

function extractYearsOfExperience(resume: string): string {
  // Try to find explicit "X+ years" mentions
  const explicit = resume.match(/(\d{1,2})\+?\s*(?:years?|yrs)/i);
  if (explicit) return `${explicit[1]}+`;

  // Try to compute from date ranges (e.g. "2015 – Present" or "2018 – 2023")
  const currentYear = new Date().getFullYear();
  const dateRanges = resume.matchAll(/(\d{4})\s*[–\-—]\s*(Present|\d{4})/gi);
  let earliestStart = currentYear;
  for (const m of dateRanges) {
    const start = parseInt(m[1], 10);
    if (start >= 1980 && start < currentYear && start < earliestStart) {
      earliestStart = start;
    }
  }
  if (earliestStart < currentYear) {
    const diff = currentYear - earliestStart;
    return `${diff}+`;
  }
  return '5+';
}

/** Blacklist of common name words and resume headers that should never be skills. */
const SKILL_BLACKLIST = new Set([
  'the', 'and', 'for', 'with', 'from', 'that', 'this', 'have', 'been', 'will',
  'name', 'email', 'phone', 'address', 'present', 'summary', 'experience',
  'education', 'skills', 'projects', 'work', 'professional', 'certifications',
  'hiring', 'manager', 'dear', 'sincerely', 'regards',
]);

function extractTopSkillsFromResume(resume: string, count: number): string {
  // Strategy 1: Look for comma-separated skills line after a "SKILLS" header
  // The resume format is: SKILLS\n───────\nSkill1, Skill2, Skill3
  const skillSectionMatch = resume.match(
    /(?:^|\n)\s*(?:skills|technical skills|core competencies|key skills|technologies)\s*\n[─\-=]*\n([^\n]+)/im
  );
  if (skillSectionMatch?.[1]) {
    const items = skillSectionMatch[1]
      .split(/[,;|•·]/)
      .map(s => s.trim())
      .filter(s => s.length > 2 && s.length < 50 && !/^[─\-=]+$/.test(s));
    if (items.length >= count) return items.slice(0, count).join(', ');
  }

  // Strategy 2: Look for a line with 3+ comma-separated technical terms
  const lines = resume.split('\n');
  for (const line of lines) {
    const items = line.split(/[,;|•·]/).map(s => s.trim()).filter(s => s.length > 2 && s.length < 50);
    if (items.length >= 4) {
      const looksLikeSkills = items.every(item =>
        !SKILL_BLACKLIST.has(item.toLowerCase()) &&
        !/^\d{4}/.test(item) &&
        !/^[─\-=]+$/.test(item)
      );
      if (looksLikeSkills) return items.slice(0, count).join(', ');
    }
  }

  // Strategy 3: Look for "Technologies:" or "Tools:" inline mentions
  const techMatch = resume.match(/(?:technologies|tools|tech stack)[:\s]+([^\n]+)/i);
  if (techMatch?.[1]) {
    const items = techMatch[1].split(/[,;|•·]/).map(s => s.trim()).filter(s => s.length > 2 && s.length < 40);
    if (items.length >= 2) return items.slice(0, count).join(', ');
  }

  // Strategy 4: Extract the job title role keywords as domain
  const roleMatch = resume.match(/^(?:##\s*)?([\w\s]+(?:Manager|Engineer|Developer|Designer|Architect|Lead|Director|Analyst))/im);
  if (roleMatch?.[1]) {
    return roleMatch[1].trim().toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  }

  return 'cross-functional leadership, strategic planning, and technical delivery';
}

async function invokeLetterDraft(
  systemPrompt: string,
  message: string,
  responseLanguage: string,
  signal?: AbortSignal,
): Promise<string> {
  const { data, error } = await supabase.functions.invoke<{
    content: string;
    error?: string;
  }>('ai-companion', {
    body: {
      message,
      history: [],
      system_override: systemPrompt,
      max_tokens: 1200,
      temperature: 0.4,
      response_language: responseLanguage,
    },
  });

  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }
  if (error) {
    throw new Error(error.message || 'Failed to generate letter');
  }
  if (!data?.content) {
    throw new Error('No content returned from ai-companion');
  }
  return cleanLetter(data.content);
}

export async function generateLetter(
  req: LetterRequest,
  signal?: AbortSignal,
): Promise<LetterResult> {
  const { mode, resume, jobTitle, company, tone = 'professional', referredBy, locale } = req;
  const responseLanguage = resolveResponseLanguage(locale);
  const systemPrompt = mode === 'cover' ? COVER_LETTER_SYSTEM : APPLICATION_LETTER_SYSTEM;
  const resumeBlock = (resume || '').slice(0, 4000);

  const brief = [
    '=== CANDIDATE RESUME ===',
    resumeBlock,
    '',
    '=== TARGET ROLE ===',
    `Position: ${jobTitle}`,
    `Company: ${company}`,
    mode === 'cover' && tone ? `Tone: ${tone}` : null,
    mode === 'application' && referredBy ? `Referred by: ${referredBy}` : null,
    '',
    `Write the complete ${mode === 'cover' ? tone + ' cover' : 'application'} letter for ${jobTitle} at ${company}.${referredBy ? ` Referred by ${referredBy}.` : ''} All 3 paragraphs must be complete sentences. Do not stop early.`,
  ]
    .filter(Boolean)
    .join('\n');

  try {
    const letter = await invokeLetterDraft(systemPrompt, brief, responseLanguage, signal);

    // If incomplete or refused, use the quality fallback immediately (no retry delay)
    if (letterLooksIncomplete(letter) || letterLooksLikeSafetyRefusal(letter)) {
      console.warn('[agentLetters] LLM output incomplete/refused, using fallback', { length: letter.length });
      return { letter: buildFallbackLetter(mode, jobTitle, company, resumeBlock, { referredBy }) };
    }

    return { letter };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err;
    }
    console.error('[agentLetters] generateLetter failed, using fallback:', err);
    return { letter: buildFallbackLetter(mode, jobTitle, company, resumeBlock, { referredBy }) };
  }
}
